// Deployment configuration
// This file allows easy switching between local development and production environments
// Cross-CRM admin pill URLs: see ./crmAppUrls.ts

/** Normalize resource URLs to HTTPS to avoid Mixed Content when page is served over HTTPS */
export function ensureHttpsResourceUrl(url: string | null | undefined): string | undefined {
  if (!url || url.startsWith('data:image/')) return url || undefined;
  if (url.startsWith('http://') && (url.includes('crm.wondertrade.pt') || url.includes('51.38.181.110'))) {
    return url.replace(/^http:\/\//i, 'https://');
  }
  return url;
}

export const DEPLOYMENT_CONFIG = {
  // Backend API URLs
  BACKEND_URLS: {
    LOCAL: 'http://localhost:5000',
    // Always use the production backend URL - environment variable is only for reference
    // The actual URL is hardcoded to prevent misconfiguration
    PRODUCTION: 'https://crm.wondertrade.pt'  // OVH dedicated server (crm.wondertrade.pt/api)
  },
  
  // Frontend URLs (HTTPS for production to avoid Mixed Content)
  FRONTEND_URLS: {
    LOCAL: 'http://localhost:3000',
    PRODUCTION: 'https://crm.wondertrade.pt',  // OVH dedicated server
    CUSTOM_DOMAIN: 'https://crm.wondertrade.pt'  // Custom domain
  },
  
  // MEO Portal URL (real portal, not proxy)
  MEO_PORTAL_URL: 'https://portal.meo.pt',
  
  // Environment detection
  isDevelopment: process.env.NODE_ENV === 'development',
  isProduction: process.env.NODE_ENV === 'production',
  
  // Get current backend URL based on environment (works in deployed and local)
  getBackendUrl: () => {
    const isDev = process.env.NODE_ENV === 'development';
    if (typeof window !== 'undefined') {
      const { hostname, port } = window.location;
      // CCC app — same-origin /api (Vite proxy in dev, reverse proxy in prod)
      if (port === '3001' || hostname.includes('ccc.')) {
        return '';
      }
      if ((hostname === 'localhost' || hostname === '127.0.0.1') && isDev) {
        return 'http://localhost:5000';
      }
    }
    if (isDev) {
      return 'http://localhost:5000';
    }
    // Production: REACT_APP_API_URL (e.g. https://crm.wondertrade.pt/api) - extract base URL
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl && apiUrl.trim()) {
      let base = apiUrl.trim().replace(/\/$/, '');
      if (base.endsWith('/api')) base = base.slice(0, -4);
      if (base.includes('crm.wondertrade.pt') || base.includes('51.38.181.110')) {
        base = base.replace(/^http:\/\//i, 'https://');
      }
      return base;
    }
    // REACT_APP_BACKEND_URL (Windows frontend → VM backend simulation)
    const explicitUrl = process.env.REACT_APP_BACKEND_URL;
    if (explicitUrl && explicitUrl.trim()) {
      let base = explicitUrl.trim().replace(/\/$/, '');
      if (base.includes('crm.wondertrade.pt') || base.includes('51.38.181.110')) {
        base = base.replace(/^http:\/\//i, 'https://');
      }
      return base;
    }
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol || 'http:';
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:5000';
      }
      if (hostname.startsWith('192.168.') || hostname.startsWith('10.') || hostname.startsWith('172.')) {
        return `${protocol}//${hostname}:5000`;
      }
      return DEPLOYMENT_CONFIG.BACKEND_URLS.PRODUCTION;
    }
    return DEPLOYMENT_CONFIG.BACKEND_URLS.PRODUCTION;
  },
  
  /** Get avatar/resource URL - uses backend URL for relative paths, normalizes HTTP to HTTPS for production */
  getResourceUrl: (pathOrUrl: string | null | undefined): string | undefined => {
    if (!pathOrUrl) return undefined;
    if (pathOrUrl.startsWith('data:image/')) return pathOrUrl;
    if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
      return ensureHttpsResourceUrl(pathOrUrl) || pathOrUrl;
    }
    return `${DEPLOYMENT_CONFIG.getBackendUrl()}/${pathOrUrl.replace(/^\//, '')}`;
  },

  // Get current frontend URL based on environment
  getFrontendUrl: () => {
    if (process.env.NODE_ENV === 'development') {
      return DEPLOYMENT_CONFIG.FRONTEND_URLS.LOCAL;
    }
    
    // Check if we're running on the custom domain
    if (typeof window !== 'undefined' && 
        window.location.hostname === 'crm.wondertrade.pt') {
      return DEPLOYMENT_CONFIG.FRONTEND_URLS.CUSTOM_DOMAIN;
    }
    
    return DEPLOYMENT_CONFIG.FRONTEND_URLS.PRODUCTION;
  }
};

// Export for easy access
export default DEPLOYMENT_CONFIG; 